import { Component, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BroadCastEvents } from '../../../common/broad-cast-events.enum';
import { MedicalReportDetailsFormRequest } from '../../../models/medical-report/medical-report-details-form-request';
import { MedicalReportDetailsFormResponse } from '../../../models/medical-report/medical-report-details-form-response';
import { BaseSecureDetails } from '../../../modules/tcm-common';
import { SecureBaseComponentContext } from '../../../modules/tcm-common/services/secure-base-component-context.service';
import { TranslationService } from '../../../services/translation.service';
import { ListOptionService } from '../../../services/list-option.service';
import { MedicalReportFormSaveResponse, MedicalReportProviderValidationRequest, AudiometryImmitanceCalculationRequest, RecommendationCalculationRequest } from '../../../models/medical-report';
import { MedicalReportDataService } from '../../../services/medical-report-data.service';
import { ListOption } from '../../../modules/tcm-core.module';
import { PureToneCategoryEnum, PureToneDegreeEnum, RecommendationSideEnum, RecommendationEquilibriumType, MedicalReportRecommendationEnum, RecommendationDurationEnum, MedicalReportBinauralDiscriminationEnum, PureToneStimulusMethodEnum, PurposeOfVisitEnum, ImmMidEarEvalTympEnum, AppointmentRecommendationEnum, MedicalReportLevelEnum } from '../../../models/enums';
import moment from 'moment';
import { NoahSessionOptionWithDate } from '../../../models/noah-sessions';
import { parse } from 'url';
import { AppointmentDataService } from '../../../services';
import { AppointmentRecommendationCheckRequest } from '../../../models/appointments';

@Component({
    selector: 'medical-report',
    templateUrl: './medical-report-details.component.html',
    styleUrls: ['./medical-report-details.component.scss'],
    standalone: false
})
export class MedicalReportDetailsComponent extends BaseSecureDetails<MedicalReportDetailsFormRequest, MedicalReportDetailsFormResponse> {
  pureToneAudCategoryOptions: ListOption<number>[];
  pureToneAudDegreeOptions: ListOption<number>[];
  recognOptions: ListOption<number>[];
  speechAudPTASatOptions: ListOption<number>[];
  pureToneAudHighFreqOptions: ListOption<number>[];
  speechAudSRTDiscOptions: ListOption<number>[];
  immMidEarReflexOptions: ListOption<number>[];
  earOptions: ListOption<number>[];
  durnOptions: ListOption<number>[];
  typeOptions: ListOption<number>[];
  recomOptions: ListOption<number>[];
  sACLevelOptions: ListOption<number>[];
  patientSatisfactionOptions: ListOption<number>[];
  medicalReportConfirmationOptions: ListOption<number>[];
  estimatedThreshholdOptions: ListOption<number>[];
  resultOptions: ListOption<number>[];
  speechAudBIDiscOptions: ListOption<number>[];
  pureToneAudStimulusMethodOptions: ListOption<number>[];
  immMidEarEvalTympOptions: ListOption<number>[];
  outcomeOptions: ListOption<number>[];
  returnOptions: ListOption<number>[];
  levelOptions: ListOption<number>[];
  noahSessionOptions: NoahSessionOptionWithDate[];

  isReadOnly: boolean = false;

  @ViewChild("validationAlertTemplate", { static: true })
  validationAlertTemplate: TemplateRef<any>;
  @ViewChild("recommendationMessageTemplate", { static: true })
  recommendationMessageTemplate: TemplateRef<any>;
  purposeOfVisitOptions: ListOption<number>[];
  appointmentNeedsUpdate: any;

  constructor(protected override componentContext: SecureBaseComponentContext, protected override route: ActivatedRoute,
    private listOptionService: ListOptionService,
    private medicalReportDataService: MedicalReportDataService,
    private appointmentDataService: AppointmentDataService,
    private translationService: TranslationService) {
    super(componentContext, route);
    this.actionName = 'MedicalReport/Details';

    const categoryOptions = this.listOptionService.getPureToneCategoryOptions();

    this.pureToneAudDegreeOptions = this.listOptionService.getPureToneDegreeOptions();
    this.pureToneAudCategoryOptions = categoryOptions.filter(x => x.value != PureToneCategoryEnum.Sensorineural_n_HA && x.value != PureToneCategoryEnum.Sensorineural_w_HA);
    this.pureToneAudHighFreqOptions = this.listOptionService.getPureToneFrequencyModifierOptions();
    this.speechAudPTASatOptions = this.listOptionService.getSpeechSATOptions();
    this.speechAudSRTDiscOptions = this.listOptionService.getSpeechSRTOptions();
    this.recognOptions = this.listOptionService.getSpeechWordRecognitionOptions();

    this.immMidEarReflexOptions = this.listOptionService.getImmMidEarReflexOptions();
    this.earOptions = this.listOptionService.getRecommendationSideOptions();
    this.durnOptions = this.listOptionService.getRecommendationDurationOptions();
    this.typeOptions = this.listOptionService.getRecommendationEquilibriumTypeOptions();
    this.recomOptions = this.listOptionService.getMedicalReportRecommendationOptions();

    this.sACLevelOptions = this.listOptionService.getSACLevelOptions();
    this.patientSatisfactionOptions = this.listOptionService.getPatientSatisfactionOptions();
    this.medicalReportConfirmationOptions = this.listOptionService.getMedicalReportConfirmationOptions();

    this.estimatedThreshholdOptions = this.pureToneAudDegreeOptions.filter(x => x.value != PureToneDegreeEnum.Slight);
    this.resultOptions = categoryOptions.filter(x => x.value != PureToneCategoryEnum.Unspecified);

    this.speechAudBIDiscOptions = this.listOptionService.getMedicalReportBinauralDiscriminationOptions();
    this.pureToneAudStimulusMethodOptions = this.listOptionService.getPureToneStimulusMethodOptions();
    this.immMidEarEvalTympOptions = this.listOptionService.getMedicalReportTympOptions();
    this.outcomeOptions = this.listOptionService.getMedicalReportOutcomeOptions();
    this.returnOptions = this.listOptionService.getMedicalReportReturnOptions();
    this.levelOptions = this.listOptionService.getMedicalReportLevelOptions();
    this.purposeOfVisitOptions = this.listOptionService.getPurposeOfVisitOptions();
  }

  openHelpTooltip(template: TemplateRef<any>) {
    this.componentContext.modalService.showActionModalAsync('', template, 'md', [
      { title: x => x.Common.SearchActionsAndDetailsActions.Close, value: 0, cssClass: 'default', isDefault: true }
    ]);
  }

  async appointmentChanged() {
    var apptDate = null;
    if (this.viewModel.form.appointmentId) {
      const appt = this.viewModel.appointmentOptions.find(x => x.value == this.viewModel.form.appointmentId);

      if (this.viewModel.providerOptions.some(x => x.value == appt.providerId)) {
        this.viewModel.form.sumTestedById = appt.providerId;
      } else {
        this.viewModel.form.sumTestedById = null;
      }

      const apptStartTime = moment(appt.startDateTime);

      apptDate = apptStartTime.format('LL');
      this.viewModel.form.dateOfTest = apptStartTime.format();

      this.viewModel.form.purposeOfVisit = appt.purposeOfVisit;
    } else {
      this.viewModel.form.sumTestedById = null;
      this.viewModel.form.dateOfTest = null;
      this.viewModel.form.purposeOfVisit = null;
    }

    await this.filterNoahByDate(apptDate, true);
  }

  public async print(): Promise<any> {
    this.isLoading = true;

    try {
      var fileResponse = await this.medicalReportDataService.downloadFileBlobAsync(this.viewModel.form.id, this.componentCancellationTokenSource.promise());
      
      await fileResponse.displayFileAsync();    
    }
    finally {
      this.isLoading = false;
    }
  }

  async updateAudiometryImmittance() {
    var ableToRun =
      this.viewModel.form.noahActionDataId && this.viewModel.form.purposeOfVisit;

    if (ableToRun) {
      var request: AudiometryImmitanceCalculationRequest = {
        noahActionId: this.viewModel.form.noahActionDataId,
        purposeOfVisit: this.viewModel.form.purposeOfVisit
      };

      var recommendationValues = await this.medicalReportDataService.getAudiometryImmitanceChangesAsync(request, this.componentCancellationTokenSource.promise());

      for (let key of Object.keys(recommendationValues)) {
        var newValue = recommendationValues[key];

        this.viewModel.form[key] = newValue;
      }
    }
  }

  updateSacTotalScore() {
    var sacValues = [this.viewModel.form.oneOnOneSituations,
                    this.viewModel.form.tvAndEntertainment,
                    this.viewModel.form.smallGroups,
                    this.viewModel.form.backgroundNoise,
                    this.viewModel.form.communicationDifficulties,
                    this.viewModel.form.environmentalSounds,
                    this.viewModel.form.negativeEffectOnSocial,
                    this.viewModel.form.annoying,
                    this.viewModel.form.otherSuggestedHearingProblems,
                    this.viewModel.form.affectsEnjoinmentOfLife];

      var sum = sacValues.reduce((a, b) => a + b, 0);

      this.viewModel.form.saCTotalScore = !sum ? 0 : (Math.ceil(parseFloat(((sum / 10) - 1).toFixed(2)) * 25));
  }

  private async checkRecommendations(): Promise<boolean> {
    var haNeeded = this.viewModel.form.recom;

    const isHaNeededDefinitelyBlank = (this.viewModel.form.drainageDur == RecommendationDurationEnum.DurS) ||
      (this.viewModel.form.equilibriumDur == RecommendationDurationEnum.DurS) ||
      (this.viewModel.form.hearinLossDur == RecommendationDurationEnum.DurS) ||
      (this.viewModel.form.otitisMediaDur == RecommendationDurationEnum.DurS) ||
      (this.viewModel.form.painDur == RecommendationDurationEnum.DurS) ||
      (this.viewModel.form.tinnitusDur == RecommendationDurationEnum.DurS);

    if (isHaNeededDefinitelyBlank) {
      haNeeded = null;
    }
    else {
      if (this.viewModel.form.purposeOfVisit) {
        var request: RecommendationCalculationRequest = {
          drainageDur: this.viewModel.form.drainageDur,
          equilibriumDur: this.viewModel.form.equilibriumDur,
          hearingAidUse: this.viewModel.form.hearingAidUse || RecommendationSideEnum.None,
          hearinLossDur: this.viewModel.form.hearinLossDur,
          noahActionId: this.viewModel.form.noahActionDataId,
          otitisMediaDur: this.viewModel.form.otitisMediaDur,
          painDur: this.viewModel.form.painDur,
          tinnitusDur: this.viewModel.form.tinnitusDur,
          purposeOfVisit: this.viewModel.form.purposeOfVisit,
          results: this.viewModel.form.results,
          pureToneAudCategoryL: this.viewModel.form.pureToneAudCategoryL,
          pureToneAudCategoryR: this.viewModel.form.pureToneAudCategoryR,
          pureToneAudDegreeL: this.viewModel.form.pureToneAudDegreeL,
          pureToneAudDegreeR: this.viewModel.form.pureToneAudDegreeR
        };

        haNeeded = await this.medicalReportDataService.getRecommendationHaNeededAsync(request, this.componentCancellationTokenSource.promise());

        this.viewModel.form.haNeeded = haNeeded;
      }
    }

    if (haNeeded != this.viewModel.form.recom) {
      const answer = await this.componentContext.modalService.showActionModalAsync(x => x.Screens.MedicalReport.ValidationMessages.HARecommendationIsDifferentTitle,
        this.recommendationMessageTemplate,
        'sm',
        [
          { title: x => x.Common.SearchActionsAndDetailsActions.Continue, value: 2, cssClass: 'complete' },
          { title: x => x.Screens.MedicalReport.Actions.Match, value: 1, cssClass: 'complete' },
          { title: x => x.Common.SearchActionsAndDetailsActions.Cancel, value: 0, cssClass: 'default', isDefault: true }
        ],
        {
          oldValue: this.translationService.translate(x => 
            this.viewModel.form.recom ? x.Enumerations.MedicalReportRecommendationEnum[this.viewModel.form.recom] : x.Screens.MedicalReport.ValidationMessages.HaNeededNull),
          newValue: this.translationService.translate(x => 
            haNeeded ? x.Enumerations.MedicalReportRecommendationEnum[haNeeded] : x.Screens.MedicalReport.ValidationMessages.HaNeededNull)
        });

      if (answer == 0) {
        return false;
      }
      else if (answer == 1) {
        this.viewModel.form.recom = haNeeded;
      }
    }

    return true;
  }

  protected override checkAdditionalCanEditRules() {
    return !this.viewModel || this.viewModel.isPatientActive;
  }

  protected override async onViewModelChanged() {
    super.onViewModelChanged();
    
    this.isReadOnly = this.viewModel.isMedicalReportLocked;

    if (this.viewModel.form.appointmentId && !this.viewModel.form.id) {
      this.appointmentChanged();
    }
    else {
      var apptDate = null;
      if (this.viewModel.form.appointmentId) {
        const appt = this.viewModel.appointmentOptions.find(x => x.value == this.viewModel.form.appointmentId);

        apptDate = moment(appt.startDateTime).format('LL');
      }

      await this.filterNoahByDate(apptDate);
    }
  }

  protected override onProcessSaveSuccess() {
    this.ngForm?.form.markAsPristine();
    this.componentContext.broadcastService.next({ name: BroadCastEvents.MedicalReportChanged });

    if (this.appointmentNeedsUpdate) {
      var recommendation = this.getAppointmentRecommendationWithMedicalConditions();

      this.componentContext.broadcastService.next({ 
        name: BroadCastEvents.AppointmentRecommendationChanged, 
        args: {
          recommendation: recommendation,
          levelRecommended: this.viewModel.form.level,
          appointmentId: this.viewModel.form.appointmentId
        } 
      });
    }

    super.onProcessSaveSuccess();
  }

  protected override async processSaveAsync(options?: any): Promise<any> {
    const canContinue = await this.checkRecommendations();

    if (!canContinue) {
      return;
    }

    const errors = await this.getValidationErrors();

    if (errors.length) {
      this.componentContext.modalService.showErrorModalAsync(x => x.Messages.MessageHeadersAndActions.Error,
        this.validationAlertTemplate,
        "md",
        null,
        errors)

      return;
    }

    await this.checkAppointmentHasDifferrentRecommendationAsync();

    return super.processSaveAsync(options);
  }

  private async checkAppointmentHasDifferrentRecommendationAsync() {
    if ((this.viewModel.form.recom && this.viewModel.form.level)
        || this.originalForm.medicalClearance != this.viewModel.form.medicalClearance
        || this.originalForm.medicalConsultation != this.viewModel.form.medicalConsultation) {
      
      var recommendation = this.getAppointmentRecommendationWithMedicalConditions();

      var checkRequest: AppointmentRecommendationCheckRequest = {
        appointmentId: this.viewModel.form.appointmentId,
        level: this.viewModel.form.level,
        recom: recommendation
      };

      this.appointmentNeedsUpdate = await this.appointmentDataService.checkAppointmentHasDifferrentRecommendationAsync(checkRequest, this.componentCancellationTokenSource.promise());

      if (this.appointmentNeedsUpdate) {
        await this.componentContext.modalService.showWarningModalAsync(
          x => x.Messages.MessageHeadersAndActions.Warning,
          x => x.Messages.WarningMessages.RecommendationAndLevelWillBeUpdatedInLinkedAppointment);
      }
    }
  }  

  private async filterNoahByDate(apptDate: string, setFirstNoah: boolean = false) {
    if (apptDate == null) {
      this.noahSessionOptions = this.viewModel.noahSessionOptions;
    }
    else {
      this.noahSessionOptions = this.viewModel.noahSessionOptions.filter(x => moment(x.date).format('LL') == apptDate);

      if (setFirstNoah && this.noahSessionOptions.length == 1) {
        const shouldUpdateAudiometryImmittance = this.viewModel.form.noahActionDataId != this.noahSessionOptions[0].value;

        this.viewModel.form.noahActionDataId = this.noahSessionOptions[0].value;

        if (shouldUpdateAudiometryImmittance) {
          await this.updateAudiometryImmittance();
        }
      }
      else if (!this.noahSessionOptions.length || !this.noahSessionOptions.some(x => x.value == this.viewModel.form.noahActionDataId)) {
        this.viewModel.form.noahActionDataId = null;
      }
    }
  }

  private async getValidationErrors(): Promise<string[]> {
    var messages = new Array<string>();

    this.isLoading = true;

    try {
      messages.push(...await this.getGeneralValidationErrors());
      messages.push(...this.getAgeValidationErrors());
      messages.push(...this.getFDAValidationErrors());
      messages.push(...this.getMedConsultValidationErrors());
      messages.push(...this.getHARecomendationValidationErrors());
      messages.push(...this.getMedClearanceValidationErrors());
      messages.push(...this.getWaxValidationErrors());
      messages.push(...this.getAudiometryImmitanceValidationErrors());
    }
    catch {
      throw messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.ValidationFailed));
    }
    finally {
      this.isLoading = false;
    }

    return messages;
  }

  private async getGeneralValidationErrors(): Promise<string[]> {
    var messages = new Array<string>();
    const currentsDate = new Date();

    if (this.viewModel.isAppointmentRequiredForMedicalReport && !this.viewModel.form.appointmentId) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.TestAppointmentIsRequired));
    }

    if (!this.viewModel.form.dateOfTest) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.DateOfTestIsRequired));
    }
    else if (moment(this.viewModel.form.dateOfTest).toDate() > currentsDate) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.DateOfTestCannotBeLaterThenToday));
    }

    if (!this.viewModel.form.sumTestedById) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.TestedByIsRequired));
    }
    else if (this.viewModel.form.appointmentId) {
      const providerValidationRequest: MedicalReportProviderValidationRequest = {
        appointmentId: this.viewModel.form.appointmentId,
        providerId: this.viewModel.form.sumTestedById
      };

      const response = await this.medicalReportDataService.getIsProviderLicensedAsync(providerValidationRequest, this.componentCancellationTokenSource.promise());

      if (!response.isAllowMedReport) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EmployeeDoesNotHaveProperCredentialsIn)
          .replace('{1}', response.state));
      }
    }

    if (moment(this.viewModel.form.lastFitted).toDate() > currentsDate) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.LastFittedCannotBeLaterThanToday));
    }

    if (!this.viewModel.form.purposeOfVisit) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.PurposeOfVisitIsRequired));
    }

    return messages;
  }

  private getAgeValidationErrors(): string[] {
    var messages = new Array<string>();

    if (this.viewModel.isPatientAdult) {
      if (this.viewModel.form.purposeOfVisit == PurposeOfVisitEnum.Pediatric) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.PurposeOfVisitCannotBePediatric));
      }

      if (this.viewModel.isPatientOld && !this.viewModel.form.yearlyEvaluation) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.YearlyEvaluationIsRequired));
      }
    }
    else {
      if (this.viewModel.form.purposeOfVisit != PurposeOfVisitEnum.Pediatric) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.PurposeOfVisitMustBePediatric));
      }
    }

    return messages;
  }

  private getFDAValidationErrors(): string[] {
    var messages = new Array<string>();

    if (!this.viewModel.form.hearinLossEar) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarHearingLossIsRequired));
    }
    else if (this.viewModel.form.hearinLossEar != RecommendationSideEnum.None && !this.viewModel.form.hearinLossDur) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarHearingLossDurationIsRequired));
    }

    if (!this.viewModel.form.painEar) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarPainIsRequired));
    }
    else if (this.viewModel.form.painEar != RecommendationSideEnum.None && !this.viewModel.form.painDur) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarPainDurationIsRequired));
    }

    if (!this.viewModel.form.drainageEar) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarDrainageIsRequired));
    }
    else if (this.viewModel.form.drainageEar != RecommendationSideEnum.None && !this.viewModel.form.drainageDur) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarDrainageDurationIsRequired));
    }

    if (!this.viewModel.form.tinnitusEar) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarTinnitusIsRequired));
    }
    else if (this.viewModel.form.tinnitusEar != RecommendationSideEnum.None && !this.viewModel.form.tinnitusDur) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarTinnitusDurationIsRequired));
    }

    if (!this.viewModel.form.equilibriumType) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarEquilibriumIsRequired));
    }
    else if (this.viewModel.form.equilibriumType != RecommendationEquilibriumType.N && !this.viewModel.form.equilibriumDur) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarEquilibriumDurationIsRequired));
    }

    if (!this.viewModel.form.otoscopicWaxEar) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarOtoscopicWaxIsRequired));
    }

    if (!this.viewModel.form.otitisMediaEar) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarOtitisMediaIsRequired));
    }
    else if (this.viewModel.form.otitisMediaEar != RecommendationSideEnum.None && !this.viewModel.form.otitisMediaDur) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.EarOtitisMediaDurationIsRequired));
    }

    if (!this.viewModel.form.prevHearLossDiagEar) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.PrevHearLossDiagIsRequired));
    }

    if (!this.viewModel.form.hearingAidUse) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.HearingAidUseIsRequired));
    }
    else if (this.viewModel.form.hearingAidUse != RecommendationSideEnum.None) {
      if (!this.viewModel.form.historyYearPurchasedAid) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.YearPurchasedIsRequired));
      }

      if (!this.viewModel.form.patientSatisfaction) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.PatientSatisfactionIsRequired));
      }
    }

    return messages;
  }

  private getMedConsultValidationErrors(): string[] {
    var messages = new Array<string>();

    if (this.viewModel.form.medicalConsultation) {
      if (this.viewModel.form.recom && this.viewModel.form.recom != MedicalReportRecommendationEnum.ContUse) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.HearingAidRecommendationCannotBeSetIfPatientRequiresMedicalConsultation));
      }

      if (!this.viewModel.form.haAfterMedConsult && this.viewModel.form.results == PureToneCategoryEnum.Sensorineural_w_HA) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.HAAfterMedConsultIsRequiredIfPatientRequiresMedicalConsultationAndResultIsSensoriWHA));
      }

      if (this.viewModel.form.results == PureToneCategoryEnum.Sensorineural_n_HA) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.MedConsultCannotBeSetIfResultIsSensoriNHA));
      }
    }
    else {
      if ([this.viewModel.form.drainageDur,
      this.viewModel.form.equilibriumDur,
      this.viewModel.form.hearinLossDur,
      this.viewModel.form.otitisMediaDur,
      this.viewModel.form.painDur,
      this.viewModel.form.tinnitusDur]
        .some(x => x == RecommendationDurationEnum.DurS)) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.MedConsultationIsRequiredIfAnyFdaDurationsAreSudden));
      }

      if (this.viewModel.form.thresholdShift) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.MedConsultationIsRequiredIfThresholdShiftIsChecked));
      }

      if ((((this.viewModel.form.pureToneAudCategoryR == PureToneCategoryEnum.Mixed) || (this.viewModel.form.pureToneAudCategoryR == PureToneCategoryEnum.Conductive))
        && ((this.viewModel.form.immMidEarEvalTympR == ImmMidEarEvalTympEnum.B) || (this.viewModel.form.immMidEarEvalTympR == ImmMidEarEvalTympEnum.C)))
        || (((this.viewModel.form.pureToneAudCategoryL == PureToneCategoryEnum.Mixed) || (this.viewModel.form.pureToneAudCategoryL == PureToneCategoryEnum.Conductive))
          && ((this.viewModel.form.immMidEarEvalTympL == ImmMidEarEvalTympEnum.B) || (this.viewModel.form.immMidEarEvalTympL == ImmMidEarEvalTympEnum.C)))) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.MedConsulationIsRequiredIfTympResultIsBOrCAndPureToneCategoryIsConductiveOrMixed));
      }
    }

    return messages;
  }

  private getHARecomendationValidationErrors(): string[] {
    var messages = new Array<string>();

    if (this.viewModel.form.recom) {
      if (!this.viewModel.form.level) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.RecommendedHearingAidTechLevelIsRequired));
      }

      if (this.viewModel.form.recom == MedicalReportRecommendationEnum.ContUse && (!this.viewModel.form.hearingAidUse || this.viewModel.form.hearingAidUse == RecommendationSideEnum.None)) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.HearingAidsRecommendationCannotBeContUseIf));
      }
    }
    else {
      if ((!this.viewModel.form.medicalConsultation || !this.viewModel.form.haAfterMedConsult || !this.viewModel.form.cerumenRemoval)
        && (this.viewModel.form.results == PureToneCategoryEnum.Sensorineural_w_HA
          || (this.viewModel.form.hearingAidUse && this.viewModel.form.hearingAidUse != RecommendationSideEnum.None))) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.HearingAidsRecommendationIsRequiredIf));
      }
    }

    return messages;
  }

  private getMedClearanceValidationErrors(): string[] {
    var messages = new Array<string>();

    if (!this.viewModel.form.medicalClearance && this.viewModel.form.recom
      && (this.viewModel.form.unilateralTinnitus || this.viewModel.form.asymmetry
        || (this.viewModel.form.immMidEarEvalTympR == ImmMidEarEvalTympEnum.A && (this.viewModel.form.pureToneAudCategoryR == PureToneCategoryEnum.Mixed || this.viewModel.form.pureToneAudCategoryR == PureToneCategoryEnum.Conductive))
        || (this.viewModel.form.immMidEarEvalTympL == ImmMidEarEvalTympEnum.A && (this.viewModel.form.pureToneAudCategoryL == PureToneCategoryEnum.Mixed || this.viewModel.form.pureToneAudCategoryL == PureToneCategoryEnum.Conductive))
        || ((this.viewModel.form.immMidEarEvalTympR == ImmMidEarEvalTympEnum.B || this.viewModel.form.immMidEarEvalTympR == ImmMidEarEvalTympEnum.C) && this.viewModel.form.pureToneAudCategoryR == PureToneCategoryEnum.Sensorineural)
        || ((this.viewModel.form.immMidEarEvalTympL == ImmMidEarEvalTympEnum.B || this.viewModel.form.immMidEarEvalTympL == ImmMidEarEvalTympEnum.C) && this.viewModel.form.pureToneAudCategoryL == PureToneCategoryEnum.Sensorineural))) {

      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.MedClearanceIsRequired));
    }

    return messages;
  }

  private getWaxValidationErrors(): string[] {
    var messages = new Array<string>();

    if (!this.viewModel.form.otoscopicWaxEar || this.viewModel.form.otoscopicWaxEar == RecommendationSideEnum.None) {
      if (this.viewModel.form.cerumenRemoval || this.viewModel.form.cerumenRemoved) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.CerumenRemovalOrRemovedCannotBeSelectedIfOtoscopicWaxIsNotPresent));
      }

      if (!this.viewModel.form.remarks && this.viewModel.form.purposeOfVisit == PurposeOfVisitEnum.HAE
        && !this.viewModel.form.pureToneAudDegreeR && !this.viewModel.form.pureToneAudCategoryR && !this.viewModel.form.pureToneAudHighFreqR
        && !this.viewModel.form.pureToneAudDegreeL && !this.viewModel.form.pureToneAudCategoryL && !this.viewModel.form.pureToneAudHighFreqL) {
        messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.RemarksAreRequiredIf));
      }
    }

    return messages;
  }

  private getAudiometryImmitanceValidationErrors(): string[] {
    var messages = new Array<string>();

    if ((this.viewModel.form.pureToneAudDegreeR && this.viewModel.form.pureToneAudDegreeR != PureToneDegreeEnum.Normal) && !this.viewModel.form.pureToneAudCategoryR) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.AudiometryPureToneRightEarCategoryIsRequired));
    }

    if ((this.viewModel.form.pureToneAudDegreeL && this.viewModel.form.pureToneAudDegreeL != PureToneDegreeEnum.Normal) && !this.viewModel.form.pureToneAudCategoryL) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.AudiometryPureToneLeftEarCategoryIsRequired));
    }

    if (this.viewModel.form.speechAudBIDiscPercent < 0 || this.viewModel.form.speechAudBIDiscPercent > 100) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.BinauralDiscriminationMustBeAValidPercentage));
    }

    if (this.viewModel.form.speechAudBIDisc != MedicalReportBinauralDiscriminationEnum.Improve && this.viewModel.form.speechAudBIDiscPercent > 0) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.BinarualDiscriminationImprovMustBeSelectedWhen));
    }

    if (this.viewModel.form.speechAudBIDisc == MedicalReportBinauralDiscriminationEnum.Improve && (!this.viewModel.form.speechAudBIDiscPercent || this.viewModel.form.speechAudBIDiscPercent == 0)) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.BinarualDiscriminationPercentageIsRequired));
    }

    if ((this.viewModel.form.pureToneAudStimulusMethod == PureToneStimulusMethodEnum.VRA || this.viewModel.form.pureToneAudStimulusMethod == PureToneStimulusMethodEnum.Play) && this.viewModel.form.soundfield) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.StimulusMethodVraOrPlayCannotBeSelectedWhen));
    }

    if (this.viewModel.form.results == null && (this.viewModel.form.pureToneAudCategoryR != PureToneCategoryEnum.Unspecified || this.viewModel.form.pureToneAudCategoryL != PureToneCategoryEnum.Unspecified)
      && ((this.viewModel.form.pureToneAudDegreeR != null && this.viewModel.form.pureToneAudCategoryR != null && this.viewModel.form.pureToneAudHighFreqR != null)
        || (this.viewModel.form.pureToneAudDegreeL != null && this.viewModel.form.pureToneAudCategoryL != null && this.viewModel.form.pureToneAudHighFreqL != null))) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.SummaryResultsIsRequiredIf));
    }

    if (this.viewModel.form.results == PureToneCategoryEnum.Normal && (this.viewModel.form.pureToneAudDegreeL != PureToneDegreeEnum.Normal || this.viewModel.form.pureToneAudDegreeR != PureToneDegreeEnum.Normal)) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.SummaryResultsCannotBeNormalIfPureToneDegreeIsNotWnl));
    }

    if (this.viewModel.form.results != PureToneCategoryEnum.Conductive 
      && this.viewModel.form.pureToneAudCategoryR == PureToneCategoryEnum.Conductive 
      && this.viewModel.form.pureToneAudCategoryL == PureToneCategoryEnum.Conductive) {
      messages.push(this.translationService.translate(x => x.Screens.MedicalReport.ValidationMessages.SummaryResultsMustBeConductiveIf));
    }

    return messages;
  }

  private getAppointmentRecommendationWithMedicalConditions(): AppointmentRecommendationEnum {
    var recom: AppointmentRecommendationEnum;
    if (this.viewModel.form.medicalClearance || this.viewModel.form.medicalConsultation) {
      recom = AppointmentRecommendationEnum.MedClearance;
    }
    else {

      switch (this.viewModel.form.recom)
      {
        case MedicalReportRecommendationEnum.Binaural:
          recom = AppointmentRecommendationEnum.Binaural;
          break;

        case MedicalReportRecommendationEnum.Left:
          recom = AppointmentRecommendationEnum.Left;
          break;

        case MedicalReportRecommendationEnum.Right:
          recom = AppointmentRecommendationEnum.Right;
          break;

        case MedicalReportRecommendationEnum.ContUse:
          recom = AppointmentRecommendationEnum.ContUse;
          break;

        default:
          recom = null;
          break;
      }
    }

    return recom;
  }
}
